import { useCallback, useState } from 'react';
import { validatePhotos, type PhotoLimits, type Rejection } from '../photo-picker/validate.js';

/** Types acceptés par défaut : les images seules (la vidéo n'est pas encore gérée par le serveur). */
export const DEFAULT_ACCEPT = 'image/*';

export interface Attachments {
  files: File[];
  /** Fichiers refusés au dernier ajout (format, taille, nombre), avec un message clair. */
  rejected: Rejection[];
  limits: PhotoLimits;
  accept: string;
  /** Ajoute des fichiers (sélecteur, appareil photo, glisser-déposer, collage), après contrôle. */
  add: (picked: File[]) => void;
  remove: (index: number) => void;
  /** Vide la sélection (après envoi). */
  clear: () => void;
}

/**
 * Pièces jointes d'un message, partagées entre la zone de saisie (`MessageComposer`) et la zone de
 * conversation où l'on peut déposer des fichiers (`AttachDropArea`). Contrôles côté interface
 * seulement (`validatePhotos`) : le serveur reste seul juge.
 */
export function useAttachments(limits: PhotoLimits = {}): Attachments {
  const [files, setFiles] = useState<File[]>([]);
  const [rejected, setRejected] = useState<Rejection[]>([]);
  const accept = limits.accept ?? DEFAULT_ACCEPT;
  const { max, maxBytes } = limits;

  const add = useCallback(
    (picked: File[]) => {
      if (picked.length === 0) return;
      const { accepted, rejected: refused } = validatePhotos(picked, files.length, {
        accept,
        ...(max !== undefined ? { max } : {}),
        ...(maxBytes !== undefined ? { maxBytes } : {}),
      });
      setRejected(refused);
      if (accepted.length) setFiles([...files, ...accepted]);
    },
    [files, accept, max, maxBytes],
  );
  const remove = useCallback((index: number) => {
    setFiles((list) => list.filter((_, i) => i !== index));
    setRejected([]);
  }, []);
  const clear = useCallback(() => {
    setFiles([]);
    setRejected([]);
  }, []);

  return { files, rejected, limits, accept, add, remove, clear };
}

/**
 * Texte envoyé avec le message, sans espaces de bord. Depuis l'intégration de la vague 7, le contrat
 * accepte un texte vide quand des photos sont jointes (`AnswerInput`, `PortalQuestionInput`) : une photo
 * seule part sans mention ajoutée. `_attachmentCount` est gardé pour les appels existants.
 */
export function messageBody(text: string, _attachmentCount: number): string {
  return text.trim();
}
